<template>
  <el-container class="bg-win-blue text-white min-h-full">
    <el-header
      class="flex items-center justify-between text-2xl px-4 py-3"
      height="60px"
    >
      <h1 class="font-serif mr-4">
        <router-link to="/admin">
          <span class="mr-3">Day Job</span> admin
        </router-link>
      </h1>
      <el-button v-if="authed" @click="logOut">
        Log Out
      </el-button>
    </el-header>
    <el-container class="bg-white text-black min-h-full">
      <transition name="fade">
        <el-aside v-if="authed" width="200px">
          <el-menu :router="true" style="height: 100%;">
            <el-submenu index="products">
              <template slot="title">
                <i class="el-icon-box" /> <span>Projects</span>
              </template>
              <el-menu-item :index="`/admin/projects`">
                Project Index
              </el-menu-item>
              <el-menu-item-group title="Project Pages">
                <el-menu-item
                  v-for="project in projects"
                  :key="project.id"
                  :index="`/admin/projects/${project.slug}`"
                >
                  {{ project.title }}
                </el-menu-item>
              </el-menu-item-group>
            </el-submenu>
            <el-menu-item :index="`/admin/types`">
              <i class="el-icon-folder-opened" /> <span>Types</span>
            </el-menu-item>
            <el-menu-item :index="`/admin/tags`">
              <i class="el-icon-price-tag" /> <span>Tags</span>
            </el-menu-item>
            <el-menu-item :index="`/admin/news`">
              <i class="el-icon-date" /> <span>News</span>
            </el-menu-item>
            <el-menu-item :index="`/admin/about`">
              <i class="el-icon-info" /> <span>About</span>
            </el-menu-item>
          </el-menu>
        </el-aside>
      </transition>
      <transition name="fade" mode="out-in">
        <router-view
          :projects="projects"
          :types="types"
          :tags="tags"
          :news="news"
          :fake-news="fakeNews"
          :key="$route.path"
        />
      </transition>
    </el-container>
  </el-container>
</template>

<script>
import Vue from 'vue'
import { mapGetters } from 'vuex'
import Element from 'element-ui'
import locale from 'element-ui/lib/locale/lang/en'
import { ADMIN_AUTHED, ADMIN_LOGOUT } from '@/store/types'
import projectsQuery from '@/admin/graphql/queries/projects.gql'
import tagsQuery from '@/admin/graphql/queries/tags.gql'
import typesQuery from '@/admin/graphql/queries/types.gql'
import newsQuery from '@/admin/graphql/queries/news.gql'
import stripTypenames from '@/admin/util/strip-typenames'
import { meta } from 'dayjob'

export default {
  name: 'Admin',

  metaInfo: {
    title: `${meta.title} Admin`,
    titleTemplate: `${meta.title} Admin - %s`
  },

  data: () => ({
    projects: []
  }),

  apollo: {
    $client: 'admin',

    projects: {
      query: projectsQuery,
      update({ projects }) {
        return stripTypenames(projects)
      },
      skip() {
        return !this.authed
      }
    },

    types: {
      query: typesQuery,
      update({ types }) {
        types = stripTypenames(types)
        return types
      },
      skip() {
        return !this.authed
      }
    },

    tags: {
      query: tagsQuery,
      update({ tags }) {
        tags = stripTypenames(tags)
        return tags
      },
      skip() {
        return !this.authed
      }
    },

    news: {
      query: newsQuery,
      update({ news }) {
        return stripTypenames(news)
      },
      skip() {
        return !this.authed
      }
    },

    fakeNews: {
      query: newsQuery,
      variables: { fake: true },
      update({ news }) {
        return stripTypenames(news)
      },
      skip() {
        return !this.authed
      }
    }
  },

  computed: {
    ...mapGetters({
      authed: `admin/${ADMIN_AUTHED}`
    })
  },

  beforeRouteEnter(to, from, next) {
    const notLogin = to.name !== 'admin-login'
    next(vm => {
      const notAuthed = !vm.authed
      if (notAuthed && notLogin) {
        next({ name: 'admin-login' })
      }
    })
  },

  beforeRouteUpdate(to, from, next) {
    const notAuthed = !this.authed
    const notLogin = to.name !== 'admin-login'
    if (notAuthed && notLogin) {
      next({ name: 'admin-login' })
    } else {
      next()
    }
  },

  created() {
    Vue.use(Element, { locale })
  },

  mounted() {
    this.$bus.$on('update-projects', this.updateProjects)
    this.$bus.$on('update-types', this.updateTypes)
    this.$bus.$on('update-tags', this.updateTags)
    this.$bus.$on('update-news', this.updateNews)
  },

  beforeDestroy() {
    this.$bus.$off('update-projects', this.updateProjects)
    this.$bus.$off('update-types', this.updateTypes)
    this.$bus.$off('update-tags', this.updateTags)
    this.$bus.$off('update-news', this.updateNews)
  },

  methods: {
    logOut() {
      this.$store.dispatch(`admin/${ADMIN_LOGOUT}`)
      this.$router.push('/admin/login')
    },

    updateProjects() {
      this.$apollo.queries.projects.refetch()
    },

    updateTypes() {
      this.$apollo.queries.types.refetch()
    },

    updateTags() {
      this.$apollo.queries.tags.refetch()
    },

    updateNews() {
      this.$apollo.queries.news.refetch()
      this.$apollo.queries.fakeNews.refetch()
    }
  }
}
</script>

<style>
@import './assets/css/element-theme/index.css';
</style>
